import { useMutation } from '@tanstack/react-query';
import {
  ENTRY_FIELDS,
  type ImportedEntry,
  type ImportMapping,
  type LookupData,
  type TableColumn,
  type TableImportPreview,
  type TableImportResult,
} from '@k-management/shared';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatValue } from './table-format.js';

const ACTION_LABELS: Record<ImportedEntry['action'], string> = {
  new: 'Nuova',
  update: 'Da aggiornare',
  unchanged: 'Già uguale',
  error: 'Errore',
};

/**
 * Loading the entries of a table from an Excel or a CSV (proposal §5): you
 * choose the file, say which column feeds which field, and see row by row what
 * would happen before anything is written. The price list lives in an Excel of
 * 87 rows: this is how it gets in, and how it is updated later.
 */
export function ImportEntriesDialog({
  tableKey,
  tableName,
  columns,
  lookups,
  autoCode,
  versionNumber,
  onClose,
  onDone,
}: {
  tableKey: string;
  tableName: string;
  columns: TableColumn[];
  /** Names of the codes of the lookup columns, to read the preview. */
  lookups: LookupData;
  /** Prefix of the codes the table gives by itself: then the column of the code may be missing. */
  autoCode: string | null;
  /** The version the rows would be written into: the draft, or the one in force. */
  versionNumber: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<string | null>(null);
  const [preview, setPreview] = useState<TableImportPreview | null>(null);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<TableImportResult | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const read = useMutation({
    mutationFn: (body: { file: string; mapping?: ImportMapping }) =>
      apiRequest<TableImportPreview>(`/tables/${tableKey}/entries/import/preview`, {
        method: 'POST',
        body,
      }),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      setSkipped(new Set());
    },
  });
  const run = useMutation({
    mutationFn: (lines: number[]) =>
      apiRequest<TableImportResult>(`/tables/${tableKey}/entries/import`, {
        method: 'POST',
        body: { file, mapping: preview?.mapping, lines },
      }),
    onSuccess: async (data) => {
      setResult(data);
      setPreview(null);
      await onDone();
    },
  });

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setFileName(chosen.name);
    const bytes = new Uint8Array(await chosen.arrayBuffer());
    let binary = '';
    for (let index = 0; index < bytes.length; index += 8192) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
    }
    const base64 = btoa(binary);
    setFile(base64);
    read.mutate({ file: base64 });
  };

  const setColumn = (field: string, column: string) => {
    if (!preview || !file) return;
    const mapping = { ...preview.mapping };
    if (column) mapping[field] = column;
    else delete mapping[field];
    setPreview({ ...preview, mapping });
    read.mutate({ file, mapping });
  };

  const entries = preview?.entries ?? [];
  const toWrite = entries.filter(
    (entry) => (entry.action === 'new' || entry.action === 'update') && !skipped.has(entry.line),
  );
  const counts = {
    new: entries.filter((entry) => entry.action === 'new').length,
    update: entries.filter((entry) => entry.action === 'update').length,
    unchanged: entries.filter((entry) => entry.action === 'unchanged').length,
    error: entries.filter((entry) => entry.action === 'error').length,
  };
  const fields = [
    ...ENTRY_FIELDS.map((field) => ({ key: field.key as string, label: field.label })),
    ...columns.map((column) => ({ key: column.key, label: column.label })),
  ];

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Importa voci in «{tableName}»</h2>
        <p className="muted">
          Le righe finiscono nella versione {versionNumber}. Un codice già presente viene
          aggiornato, non duplicato.
        </p>

        <Field label="File" hint="Excel (.xlsx) o CSV, con la riga delle intestazioni in cima.">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(event) => void choose(event)} />
        </Field>
        {fileName && <p className="muted small">{fileName}</p>}
        {read.isPending && <p className="muted">Lettura del file…</p>}
        {read.isError && <p className="error">{read.error.message}</p>}

        {preview && (
          <>
            <h3>Da quale colonna prendere ogni campo</h3>
            <div className="form-grid">
              {fields.map((field) => (
                <Field
                  key={field.key}
                  label={field.label}
                  error={
                    field.key === 'code' && !preview.mapping.code && !autoCode
                      ? 'Campo obbligatorio'
                      : undefined
                  }
                >
                  <select
                    value={preview.mapping[field.key] ?? ''}
                    onChange={(event) => setColumn(field.key, event.target.value)}
                  >
                    <option value="">—</option>
                    {preview.fileColumns.map((column) => (
                      <option key={column} value={column}>
                        {column}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>

            {!preview.mapping.code && autoCode && (
              <p className="notice info">
                Senza la colonna del codice, le righe ricevono i codici da sole ({autoCode}-0001,{' '}
                {autoCode}-0002…) e sono tutte voci nuove.
              </p>
            )}
            {!preview.mapping.code && !autoCode && (
              <p className="notice">
                Scegli quale colonna del file contiene il <strong>codice</strong> della voce: è
                quello che distingue una riga dall’altra.
              </p>
            )}

            {entries.length > 0 && (
              <>
                <p>
                  <strong>{counts.new}</strong> nuove · <strong>{counts.update}</strong> da
                  aggiornare · {counts.unchanged} già uguali
                  {counts.error > 0 && (
                    <>
                      {' '}
                      · <span className="warn-text">{counts.error} con errori</span>
                    </>
                  )}
                </p>
                <div className="table-wrap import-preview">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th />
                        <th>Riga</th>
                        <th>Codice</th>
                        <th>Descrizione</th>
                        {columns.map((column) => (
                          <th key={column.key}>{column.label}</th>
                        ))}
                        <th>Cosa succede</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => {
                        const writable = entry.action === 'new' || entry.action === 'update';
                        return (
                          <tr
                            key={entry.line}
                            className={entry.action === 'error' ? 'inactive' : undefined}
                          >
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Importa la riga ${entry.line}`}
                                disabled={!writable}
                                checked={writable && !skipped.has(entry.line)}
                                onChange={() =>
                                  setSkipped((current) => {
                                    const next = new Set(current);
                                    if (next.has(entry.line)) next.delete(entry.line);
                                    else next.add(entry.line);
                                    return next;
                                  })
                                }
                              />
                            </td>
                            <td>{entry.line}</td>
                            <td className="code">{entry.code || '—'}</td>
                            <td>{entry.description}</td>
                            {columns.map((column) => (
                              <td key={column.key} className="numeric">
                                {formatValue(
                                  column,
                                  entry.values[column.key] ?? null,
                                  false,
                                  lookups,
                                )}
                              </td>
                            ))}
                            <td>
                              {ACTION_LABELS[entry.action]}
                              {entry.message && (
                                <small className="muted block">{entry.message}</small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {result && (
          <p className="notice ok-notice">
            Importate: <strong>{result.created}</strong> voci nuove e{' '}
            <strong>{result.updated}</strong> aggiornate
            {result.errors.length > 0 && `, ${result.errors.length} righe saltate`}.
          </p>
        )}
        {run.isError && <p className="error">{run.error.message}</p>}

        <div className="actions">
          <button type="button" onClick={onClose}>
            {result ? 'Chiudi' : 'Annulla'}
          </button>
          {!result && (
            <button
              type="button"
              className="primary"
              disabled={toWrite.length === 0 || run.isPending}
              onClick={() => run.mutate(toWrite.map((entry) => entry.line))}
            >
              {run.isPending ? 'Importazione…' : `Importa ${toWrite.length} righe`}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}
